import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    UIAnimation,
    UICheckbox,
    UIFormInput,
    UIIcon,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UIPaginatedSelectInput,
    UIPaginatedSelectOption,
    UIQRScanner,
    UISpacer,
    UISpinner,
    UIText,
    UIToast
} from '@/ui';
import {
    backgroundMedium,
    blue,
    blue10,
    blue20,
    concrete,
    moonlight,
    red,
    redLight,
    white
} from '@/ui/styles';
import checkedDone from '../assets/lotties/checked-done.json';
import fallIcon from '../assets/images/fall.png';
import tiltIcon from '../assets/images/tilt.png';
import lwdFront from '../assets/images/lwd-front.svg';
import lwdRear from '../assets/images/lwd-rear.svg';
import { useCompanyContext } from '@/companies/providers';
import { useFetchTeamOptions } from '@/companies/queries';
import {
    useActivateDeviceAction,
    useConfigureActivatedDeviceAction
} from '../actions';
import { useBulkAlertLicenses, useFetchTeamMemberOptions } from '../queries';
import { useDevicesTranslation } from '../hooks';
import { FeatureIconWithDot } from './feature-icon-with-dot';

type ActivateDeviceModalProps = {
    companyId: string;
    isOpen: boolean;
    onClose: () => void;
};

type Step = 'scan' | 'details' | 'alerts' | 'success' | 'error';

/**
 * The whole activation flow: scan the device's QR code, claim it for the
 * company, name it, turn its alerts on, and confirm. Every step lives here
 * because they are one thing a user completes, and each one only makes sense
 * as part of the run.
 */
export function ActivateDeviceModal(
    props: ActivateDeviceModalProps
): ReactElement {
    const { isOpen, onClose } = props;

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <ModalContent {...props} /> : null}
        </UIModal>
    );
}

function ModalContent(props: ActivateDeviceModalProps): ReactElement {
    const { companyId, onClose } = props;

    const { company } = useCompanyContext();

    const [step, setStep] = useState<Step>('scan');
    const [deviceId, setDeviceId] = useState<string | null>(null);
    const [enabledAlerts, setEnabledAlerts] = useState<{
        fall: boolean;
        tilt: boolean;
    }>({ fall: false, tilt: false });

    // Claiming the device is owned here rather than in the scan step, because
    // the error screen is a sibling step and reads the same `error`.
    const { activateDevice, loading, error } = useActivateDeviceAction();

    // The alerts step only offers fall/tilt/geofence setup. If the company owns
    // none of those addons there is nothing to configure, so skip it entirely.
    const lwd = company.lwdSubscription;
    const hasAnyAlertAddon =
        (lwd?.fallQuantity ?? 0) > 0 ||
        (lwd?.tiltQuantity ?? 0) > 0 ||
        (lwd?.geoQuantity ?? 0) > 0;

    function reset() {
        setDeviceId(null);
        setEnabledAlerts({ fall: false, tilt: false });
        setStep('scan');
    }

    if (step === 'details' && deviceId) {
        return (
            <DetailsStep
                deviceId={deviceId}
                companyId={companyId}
                onNext={() => setStep(hasAnyAlertAddon ? 'alerts' : 'success')}
                onCancel={onClose}
            />
        );
    }

    if (step === 'alerts' && deviceId) {
        return (
            <AlertsStep
                deviceId={deviceId}
                companyId={companyId}
                onComplete={(enabled) => {
                    setEnabledAlerts(enabled);
                    setStep('success');
                }}
                onCancel={onClose}
            />
        );
    }

    if (step === 'success' && deviceId) {
        return (
            <SuccessStep
                deviceId={deviceId}
                companyId={companyId}
                enabledAlerts={enabledAlerts}
                onActivateAnother={reset}
                onClose={onClose}
            />
        );
    }

    if (step === 'error') {
        return (
            <ErrorStep
                message={error}
                onActivateAnother={reset}
                onClose={onClose}
            />
        );
    }

    return (
        <ScanStep
            loading={loading}
            onClose={onClose}
            onActivate={(scannedId) => {
                activateDevice({ deviceId: scannedId, data: { companyId } })
                    .then((device) => {
                        setDeviceId(device.id);
                        setStep('details');
                    })
                    // The reason is on the action's `error`, which the error
                    // step reads.
                    .catch(() => setStep('error'));
            }}
        />
    );
}

/**
 * Three phases:
 *  - intro:    explain where the QR code is, with a button to reveal the camera.
 *  - scanning: the live camera scanner.
 *  - scanned:  the captured device id, confirmed, with a "Next" that activates.
 *
 * Scanning only captures the id locally; nothing is committed on the backend
 * until the user presses Next, so "Scan again" is always safe.
 */
type ScanPhase = 'intro' | 'scanning' | 'scanned';

function ScanStep(props: {
    loading: boolean;
    onClose: () => void;
    onActivate: (deviceId: string) => void;
}): ReactElement {
    const { loading, onClose, onActivate } = props;
    const { t } = useDevicesTranslation();

    const [phase, setPhase] = useState<ScanPhase>('intro');
    const [deviceId, setDeviceId] = useState<string | null>(null);

    function takeDeviceIdFromUrl(data: string): string | null {
        // Data should look like this: https://device.safepointapp.com/?id=657867a46e14e074d4d38fa1
        try {
            const url = new URL(data);
            return url.searchParams.get('id');
        } catch {
            return null;
        }
    }

    function handleScan(data: string) {
        const scannedId = takeDeviceIdFromUrl(data);
        if (!scannedId) {
            UIToast.error(t('shared.errors.invalid_qr_code'));
            return;
        }
        setDeviceId(scannedId);
        setPhase('scanned');
    }

    if (phase === 'scanning') {
        return (
            <>
                <UIModalHeader
                    title={t('shared.devices.activate_device_label')}
                    subtitle={t(
                        'components.activate_device_modal.scan_camera_subtitle'
                    )}
                />
                <UISpacer />
                <UIQRScanner onScan={handleScan} />
                <UIModalFooter
                    actions={[
                        {
                            type: 'button',
                            text: t('shared.labels.back'),
                            onClick: () => setPhase('intro')
                        },
                        {
                            type: 'button',
                            text: t('shared.labels.close'),
                            onClick: onClose
                        }
                    ]}
                />
            </>
        );
    }

    if (phase === 'scanned' && deviceId) {
        return (
            <>
                <UIModalHeader
                    title={t(
                        'components.activate_device_modal.scan_detected_title'
                    )}
                    subtitle={t(
                        'components.activate_device_modal.scan_detected_subtitle'
                    )}
                />
                <UIModalContent>
                    <ScannedDeviceCard deviceId={deviceId} />
                </UIModalContent>
                <UIModalFooter
                    actions={[
                        {
                            variant: 'primary',
                            type: 'button',
                            text: t('shared.labels.next'),
                            loading: loading,
                            disabled: loading,
                            onClick: () => onActivate(deviceId)
                        },
                        {
                            type: 'button',
                            text: t(
                                'components.activate_device_modal.scan_again'
                            ),
                            disabled: loading,
                            onClick: () => {
                                setDeviceId(null);
                                setPhase('scanning');
                            }
                        }
                    ]}
                />
            </>
        );
    }

    // intro
    return (
        <>
            <UIModalHeader
                title={t('shared.devices.activate_device_label')}
                subtitle={t(
                    'components.activate_device_modal.scan_instructions'
                )}
            />
            <UIModalContent>
                <DeviceQrGraphic />
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'button',
                        leftIcon: 'camera',
                        text: t('shared.labels.scan_qr_code'),
                        onClick: () => setPhase('scanning')
                    },
                    {
                        type: 'button',
                        text: t('shared.labels.close'),
                        onClick: onClose
                    }
                ]}
            />
        </>
    );
}

/**
 * Illustration of the device turning from front to back, where the QR code
 * lives. The rotate badge between the two views hints at flipping it over, and
 * the back (QR) side is gently highlighted to draw the eye.
 */
function DeviceQrGraphic(): ReactElement {
    const { t } = useDevicesTranslation();

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 24,
                padding: '40px 24px',
                borderRadius: 20,
                background: `linear-gradient(160deg, ${blue10} 0%, ${backgroundMedium} 100%)`
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 20
                }}
            >
                <DeviceTile src={lwdFront} />
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 999,
                        background: blue,
                        boxShadow: `0 6px 16px ${blue20}`,
                        flexShrink: 0
                    }}
                >
                    <UIIcon name="refresh-cw" size={24} color={white} />
                </div>
                <DeviceTile src={lwdRear} highlight />
            </div>
            <UIText type="default2" textAlign="center">
                {t('components.activate_device_modal.scan_graphic_caption')}
            </UIText>
        </div>
    );
}

/** A device illustration resting on an elevated, rounded white tile. */
function DeviceTile(props: { src: string; highlight?: boolean }): ReactElement {
    const { src, highlight = false } = props;
    return (
        <div
            style={{
                padding: 16,
                borderRadius: 20,
                background: white,
                border: `${highlight ? 2 : 1}px solid ${
                    highlight ? blue : moonlight
                }`,
                boxShadow: highlight
                    ? `0 10px 28px ${blue20}`
                    : '0 8px 24px rgba(28, 28, 28, 0.08)'
            }}
        >
            <img src={src} alt="" style={{ height: 180, width: 'auto' }} />
        </div>
    );
}

/** Shows the captured device id as reassurance before activating. */
function ScannedDeviceCard(props: { deviceId: string }): ReactElement {
    const { deviceId } = props;
    const { t } = useDevicesTranslation();

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                padding: 24,
                borderRadius: 16,
                background: blue10
            }}
        >
            <UIText variant="small" type="default2">
                {t('components.activate_device_modal.scan_device_id_label')}
            </UIText>
            <UIText medium textAlign="center">
                {deviceId}
            </UIText>
        </div>
    );
}

/**
 * Name the device and assign a team and an optional member. "Next" saves these,
 * then moves on to the alerts page.
 */
function DetailsStep(props: {
    /** The already-activated device id. */
    deviceId: string;
    companyId: string;
    /** Called once the name/team/member have been saved. */
    onNext: () => void;
    onCancel: () => void;
}): ReactElement {
    const { deviceId, companyId, onNext, onCancel } = props;
    const { t } = useDevicesTranslation();

    const { saveDetails, loading } = useConfigureActivatedDeviceAction();

    const [showErrors, setShowErrors] = useState(false);
    const [name, setName] = useState(
        t('components.activate_device_modal.details_name_default')
    );
    const [team, setTeam] = useState<UIPaginatedSelectOption | undefined>(
        undefined
    );
    const [user, setUser] = useState<UIPaginatedSelectOption | undefined>(
        undefined
    );

    const teamPicker = useFetchTeamOptions({ companyId });
    const memberOptions = useFetchTeamMemberOptions({
        companyId,
        teamId: team?.value
    });

    const nameError =
        name.trim().length === 0 ? t('shared.fields.name.error_required') : '';
    const teamError = !team ? t('shared.fields.team.error_required') : '';

    function handleNext() {
        setShowErrors(true);
        if (nameError || teamError || !team) {
            return;
        }

        saveDetails(deviceId, {
            name: name.trim(),
            teamId: team.value,
            companyUserId: user?.value ?? null
        })
            .then(() => {
                onNext();
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <>
            <UIModalHeader
                title={t('components.activate_device_modal.details_title')}
                subtitle={t(
                    'components.activate_device_modal.details_subtitle'
                )}
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <UIFormInput
                        label={t('shared.fields.name.label')}
                        description={t('shared.devices.name_description')}
                        type="text"
                        value={name}
                        onChange={setName}
                        required
                        maxLength={1000}
                        errorText={showErrors ? nameError : ''}
                    />

                    <UIPaginatedSelectInput
                        label={t('shared.fields.team.label')}
                        placeholder={t('shared.fields.team.placeholder')}
                        description={t('shared.devices.team_description')}
                        searchPlaceholder={t(
                            'shared.fields.team.search_placeholder'
                        )}
                        emptyText={t('shared.fields.team.empty')}
                        errorStateText={t('shared.fields.team.load_error')}
                        required
                        options={teamPicker.options}
                        value={team}
                        onChange={(value) => {
                            setTeam(value);
                            setUser(undefined);
                        }}
                        search={teamPicker.search}
                        onSearchChange={teamPicker.setSearch}
                        isLoading={teamPicker.status === 'pending'}
                        isError={teamPicker.status === 'error'}
                        isFetchingNextPage={teamPicker.isFetchingNextPage}
                        hasNextPage={teamPicker.hasNextPage}
                        onLoadMore={teamPicker.fetchNextPage}
                        onRetry={teamPicker.refetch}
                        errorText={showErrors ? teamError : ''}
                    />

                    {team && (
                        <UIPaginatedSelectInput
                            label={t('shared.fields.member.label_optional')}
                            placeholder={t('shared.fields.member.placeholder')}
                            description={t('shared.fields.member.description')}
                            searchPlaceholder={t(
                                'shared.fields.member.search_placeholder'
                            )}
                            emptyText={t('shared.fields.member.empty')}
                            errorStateText={t(
                                'shared.fields.member.load_error'
                            )}
                            clearText={t('shared.fields.member.clear')}
                            value={user}
                            onChange={setUser}
                            options={memberOptions.options}
                            search={memberOptions.search}
                            onSearchChange={memberOptions.setSearch}
                            isLoading={memberOptions.status === 'pending'}
                            isError={memberOptions.status === 'error'}
                            isFetchingNextPage={
                                memberOptions.isFetchingNextPage
                            }
                            hasNextPage={memberOptions.hasNextPage}
                            onLoadMore={memberOptions.fetchNextPage}
                            onRetry={memberOptions.refetch}
                        />
                    )}
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'button',
                        text: t('shared.labels.next'),
                        loading: loading,
                        disabled: loading,
                        onClick: handleNext
                    },
                    {
                        type: 'button',
                        text: t('shared.labels.cancel'),
                        disabled: loading,
                        onClick: onCancel
                    }
                ]}
            />
        </>
    );
}

/**
 * Enable fall/tilt detection (checkboxes default on when the company owns the
 * addon and a seat is free, disabled with a reason when the limit is reached).
 * Geofence can't be enabled here (it needs a drawn area), so it is highlighted
 * as something to configure afterwards on the device page.
 */
function AlertsStep(props: {
    /** The already-activated device id (details already saved). */
    deviceId: string;
    companyId: string;
    /** Called once the selected alert features have been saved, with which were enabled. */
    onComplete: (enabled: { fall: boolean; tilt: boolean }) => void;
    onCancel: () => void;
}): ReactElement {
    const { deviceId, companyId, onComplete, onCancel } = props;
    const { t } = useDevicesTranslation();

    const deviceIds = useMemo(() => [deviceId], [deviceId]);
    const licenses = useBulkAlertLicenses({ companyId, deviceIds });
    const { saveAlerts, loading } = useConfigureActivatedDeviceAction();

    const fallOwned = licenses.fall.limit > 0;
    const tiltOwned = licenses.tilt.limit > 0;
    const geoOwned = licenses.geo.limit > 0;
    const fallSeat = licenses.fall.free > 0;
    const tiltSeat = licenses.tilt.free > 0;

    const [fall, setFall] = useState(false);
    const [tilt, setTilt] = useState(false);

    // Seat counts aren't known until the usage query resolves, so default the
    // checkboxes (on when a seat is free) once, after it loads.
    const seededRef = useRef(false);
    useEffect(() => {
        if (!licenses.loading && !seededRef.current) {
            seededRef.current = true;
            setFall(fallSeat);
            setTilt(tiltSeat);
        }
    }, [licenses.loading, fallSeat, tiltSeat]);

    function handleSave() {
        const enabledFall = fallOwned && fallSeat && fall;
        const enabledTilt = tiltOwned && tiltSeat && tilt;
        saveAlerts(deviceId, {
            fall: enabledFall,
            tilt: enabledTilt
        })
            .then(() => {
                onComplete({ fall: enabledFall, tilt: enabledTilt });
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <>
            <UIModalHeader
                title={t('components.activate_device_modal.alerts_title')}
                subtitle={t('components.activate_device_modal.alerts_subtitle')}
            />
            <UIModalContent>
                {licenses.loading ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: 24
                        }}
                    >
                        <UISpinner size={24} />
                    </div>
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16
                        }}
                    >
                        {fallOwned && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UICheckbox
                                    checked={fallSeat && fall}
                                    disabled={!fallSeat || loading}
                                    onChange={setFall}
                                    description={t('shared.devices.fall_label')}
                                />
                                <div
                                    style={{
                                        paddingLeft: 36,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 6
                                    }}
                                >
                                    <UIText variant="small" type="default2">
                                        {t('shared.devices.fall_description')}
                                    </UIText>
                                    <UIText variant="small" type="default2">
                                        {fallSeat
                                            ? t(
                                                  'components.activate_device_modal.alerts_available_count',
                                                  {
                                                      available:
                                                          licenses.fall.free,
                                                      total: licenses.fall.limit
                                                  }
                                              )
                                            : t(
                                                  'components.activate_device_modal.alerts_fall_limit_reached',
                                                  { count: licenses.fall.limit }
                                              )}
                                    </UIText>
                                </div>
                            </div>
                        )}
                        {tiltOwned && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UICheckbox
                                    checked={tiltSeat && tilt}
                                    disabled={!tiltSeat || loading}
                                    onChange={setTilt}
                                    description={t('shared.devices.tilt_label')}
                                />
                                <div
                                    style={{
                                        paddingLeft: 36,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 6
                                    }}
                                >
                                    <UIText variant="small" type="default2">
                                        {t('shared.devices.tilt_description')}
                                    </UIText>
                                    <UIText variant="small" type="default2">
                                        {tiltSeat
                                            ? t(
                                                  'components.activate_device_modal.alerts_available_count',
                                                  {
                                                      available:
                                                          licenses.tilt.free,
                                                      total: licenses.tilt.limit
                                                  }
                                              )
                                            : t(
                                                  'components.activate_device_modal.alerts_tilt_limit_reached',
                                                  { count: licenses.tilt.limit }
                                              )}
                                    </UIText>
                                </div>
                            </div>
                        )}

                        {geoOwned && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4,
                                    padding: 12,
                                    borderRadius: 8,
                                    border: `1px solid ${concrete}`,
                                    backgroundColor: backgroundMedium
                                }}
                            >
                                <UIText medium>
                                    {t('shared.devices.geofence_label')}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.activate_device_modal.alerts_geofence_highlight'
                                    )}
                                </UIText>
                            </div>
                        )}
                    </div>
                )}
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'button',
                        text: t('shared.labels.save'),
                        loading: loading,
                        disabled: loading || licenses.loading,
                        onClick: handleSave
                    },
                    {
                        type: 'button',
                        text: t('shared.labels.cancel'),
                        disabled: loading,
                        onClick: onCancel
                    }
                ]}
            />
        </>
    );
}

/**
 * Confirms the device is set up, explains the settings are on their way to the
 * device, and points the user to the device page to add a geofence (if the
 * addon is owned) and Wi-Fi.
 */
function SuccessStep(props: {
    deviceId: string;
    companyId: string;
    /** Which alert features were enabled during activation (queued on the device). */
    enabledAlerts: { fall: boolean; tilt: boolean };
    onActivateAnother: () => void;
    onClose: () => void;
}): ReactElement {
    const { deviceId, companyId, enabledAlerts, onActivateAnother, onClose } =
        props;
    const { t } = useDevicesTranslation();
    const navigate = useNavigate();
    const { company } = useCompanyContext();

    const geoOwned = (company.lwdSubscription?.geoQuantity ?? 0) > 0;
    const showPendingHint = enabledAlerts.fall || enabledAlerts.tilt;

    function openDevice() {
        void navigate(`/company/${companyId}/settings/devices/${deviceId}`);
        onClose();
    }

    return (
        <>
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 16
                    }}
                >
                    <div
                        style={{
                            height: 150,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <UIAnimation
                            animationData={checkedDone}
                            loop={false}
                            style={{ height: 400 }}
                        />
                    </div>
                    <UIText variant="h2" bold textAlign="center">
                        {t('components.activate_device_modal.success_title')}
                    </UIText>
                    <UIText textAlign="center">
                        {t('components.activate_device_modal.success_message')}
                    </UIText>
                    {showPendingHint && (
                        <div
                            style={{
                                alignSelf: 'stretch',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 14,
                                padding: 16,
                                borderRadius: 12,
                                border: `1px solid ${moonlight}`,
                                backgroundColor: backgroundMedium
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    gap: 8,
                                    flexShrink: 0
                                }}
                            >
                                {enabledAlerts.fall && (
                                    <FeatureIconWithDot pending>
                                        <FeatureChip
                                            src={fallIcon}
                                            alt={t('shared.devices.fall_label')}
                                        />
                                    </FeatureIconWithDot>
                                )}
                                {enabledAlerts.tilt && (
                                    <FeatureIconWithDot pending>
                                        <FeatureChip
                                            src={tiltIcon}
                                            alt={t('shared.devices.tilt_label')}
                                        />
                                    </FeatureIconWithDot>
                                )}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2
                                }}
                            >
                                <UIText medium>
                                    {t(
                                        'components.activate_device_modal.success_pending_title'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.activate_device_modal.success_pending_body'
                                    )}
                                </UIText>
                            </div>
                        </div>
                    )}
                    <UIText textAlign="center" type="default2">
                        {geoOwned
                            ? t(
                                  'components.activate_device_modal.success_guidance_with_geofence'
                              )
                            : t(
                                  'components.activate_device_modal.success_guidance'
                              )}
                    </UIText>
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'button',
                        text: t(
                            'components.activate_device_modal.success_open_device'
                        ),
                        onClick: openDevice
                    },
                    {
                        type: 'button',
                        text: t('shared.devices.activate_another'),
                        onClick: onActivateAnother
                    },
                    {
                        type: 'button',
                        text: t('shared.labels.close'),
                        onClick: onClose
                    }
                ]}
            />
        </>
    );
}

/** A feature icon resting on a rounded white tile, matching the devices list. */
function FeatureChip(props: { src: string; alt: string }): ReactElement {
    const { src, alt } = props;
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 10,
                background: white,
                border: `1px solid ${moonlight}`
            }}
        >
            <img src={src} alt={alt} style={{ width: 22, height: 22 }} />
        </div>
    );
}

/** Activation failed; the reason comes from the action that failed. */
function ErrorStep(props: {
    message: string;
    onActivateAnother: () => void;
    onClose: () => void;
}): ReactElement {
    const { message, onActivateAnother, onClose } = props;
    const { t } = useDevicesTranslation();

    return (
        <>
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 16,
                        padding: '32px 0'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 72,
                            height: 72,
                            borderRadius: 999,
                            background: redLight
                        }}
                    >
                        <UIIcon name="alert-triangle" size={34} color={red} />
                    </div>
                    <UIText variant="h2" bold textAlign="center">
                        {t('components.activate_device_modal.error_title')}
                    </UIText>
                    <UIText textAlign="center" type="default2">
                        {message}
                    </UIText>
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'button',
                        text: t('shared.devices.activate_another'),
                        onClick: onActivateAnother
                    },
                    {
                        type: 'button',
                        text: t('shared.labels.close'),
                        onClick: onClose
                    }
                ]}
            />
        </>
    );
}
